/**
 * Calendar Events - Core Types
 * Based on the CalendarEventsFrontendSpec
 */

import { PaginatedResponse } from './common'

// ====================
// ENUMS
// ====================

/**
 * Owner entity type for events
 */
export enum EventOwnerEntityType {
  User = 0,
  Group = 1,
  Business = 2,
}

/**
 * Event status
 */
export enum CalendarEventStatus {
  Draft = 1,
  Published = 2,
  Cancelled = 3,
  Completed = 4,
}

/**
 * RSVP/Attendance status
 */
export enum RsvpStatus {
  Attending = 1,
  Tentative = 2,
  NotAttending = 3,
}

// ====================
// HELPER FUNCTIONS
// ====================

/**
 * Get display label for event status
 */
export function getEventStatusLabel(status: CalendarEventStatus | number | undefined): string {
  if (status === undefined || status === null) return 'Unknown'
  const numStatus = typeof status === 'number' ? status : Number(status)
  switch (numStatus) {
    case CalendarEventStatus.Draft:
      return 'Draft'
    case CalendarEventStatus.Published:
      return 'Published'
    case CalendarEventStatus.Cancelled:
      return 'Cancelled'
    case CalendarEventStatus.Completed:
      return 'Completed'
    default:
      return String(status)
  }
}

/**
 * Get chip color for event status
 */
export function getEventStatusColor(
  status: CalendarEventStatus | number | undefined
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  if (status === undefined || status === null) return 'default'
  const numStatus = typeof status === 'number' ? status : Number(status)
  switch (numStatus) {
    case CalendarEventStatus.Draft:
      return 'warning'
    case CalendarEventStatus.Published:
      return 'success'
    case CalendarEventStatus.Cancelled:
      return 'error'
    case CalendarEventStatus.Completed:
      return 'default'
    default:
      return 'default'
  }
}

/**
 * Get display label for RSVP status
 */
export function getRsvpStatusLabel(status: RsvpStatus | number | undefined): string {
  if (status === undefined || status === null) return 'Unknown'
  const numStatus = typeof status === 'number' ? status : Number(status)
  switch (numStatus) {
    case RsvpStatus.Attending:
      return 'Attending'
    case RsvpStatus.Tentative:
      return 'Maybe'
    case RsvpStatus.NotAttending:
      return "Can't Go"
    default:
      return String(status)
  }
}

/**
 * Get chip color for RSVP status
 */
export function getRsvpStatusColor(
  status: RsvpStatus | number | undefined
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  if (status === undefined || status === null) return 'default'
  const numStatus = typeof status === 'number' ? status : Number(status)
  switch (numStatus) {
    case RsvpStatus.Attending:
      return 'success'
    case RsvpStatus.Tentative:
      return 'warning'
    case RsvpStatus.NotAttending:
      return 'error'
    default:
      return 'default'
  }
}

/**
 * Get owner type label
 */
export function getOwnerTypeLabel(type: EventOwnerEntityType | number | undefined): string {
  if (type === undefined || type === null) return 'Unknown'
  const numType = typeof type === 'number' ? type : Number(type)
  switch (numType) {
    case EventOwnerEntityType.User:
      return 'User'
    case EventOwnerEntityType.Group:
      return 'Group'
    case EventOwnerEntityType.Business:
      return 'Business'
    default:
      return String(type)
  }
}

// ====================
// DTOs
// ====================

/**
 * Owner entity info
 */
export interface EventOwnerDto {
  entityId: string
  entityType: EventOwnerEntityType
  entityName: string
  entityAvatarUrl?: string
}

/**
 * Creator info (may differ from owner for group/business events)
 */
export interface EventCreatorDto {
  userId: string
  displayName: string
  profileImageUrl?: string
}

/**
 * Location info for events
 */
export interface EventLocationDto {
  name?: string
  latitude?: number
  longitude?: number
  address?: string
}

/**
 * Linked adventure info
 */
export interface LinkedAdventureDto {
  adventureId: string
  title: string
  thumbnailUrl?: string
}

/**
 * Attendance stats
 */
export interface EventAttendanceStatsDto {
  attendingCount: number
  tentativeCount: number
  notAttendingCount: number
  totalRsvpCount: number
}

/**
 * User's RSVP info
 */
export interface UserRsvpDto {
  status: RsvpStatus
  note?: string
  rsvpDate: string
}

/**
 * Main Calendar Event DTO
 */
export interface CalendarEventDto {
  id: string
  title: string
  quickDescription: string
  fullDescription?: string
  status: CalendarEventStatus

  // Date/Time
  startDateTime: string
  endDateTime: string
  timezone?: string
  isAllDay: boolean

  // Location
  location?: EventLocationDto

  // Privacy & Capacity
  isPublic: boolean
  maxAttendees?: number

  // Owner/Creator
  owner: EventOwnerDto
  creator: EventCreatorDto

  // Images
  coverImageUrl?: string
  coverImageId?: string

  // Tags
  tags: string[]

  // Linked adventure
  linkedAdventure?: LinkedAdventureDto

  // Attendance
  attendanceStats: EventAttendanceStatsDto
  userRsvp?: UserRsvpDto
  isEventFull: boolean

  // Dates
  createdDate: string
  modifiedDate: string

  // Permissions
  canEdit?: boolean
  canDelete?: boolean
}

/**
 * Attendee DTO
 */
export interface EventAttendeeDto {
  userId: string
  displayName: string
  profileImageUrl?: string
  status: RsvpStatus
  note?: string
  rsvpDate: string
}

/**
 * Subscription limits for user events
 */
export interface EventSubscriptionLimitsDto {
  maxEventsPerMonth: number
  eventsCreatedThisMonth: number
  maxEventSize: number
  canCreateEvent: boolean
}

// ====================
// REQUEST TYPES
// ====================

/**
 * Parameters for searching/listing events
 */
export interface CalendarEventSearchParams {
  page?: number
  pageSize?: number
  searchTerm?: string
  status?: CalendarEventStatus
  startDateFrom?: string
  startDateTo?: string
  tags?: string[]
  ownerEntityId?: string
  ownerEntityType?: EventOwnerEntityType
  adventureId?: string
  isPublic?: boolean
  sortBy?: 'startDate' | 'createdDate' | 'title'
  sortDirection?: 'asc' | 'desc'
}

/**
 * Request to create a new event
 */
export interface CreateCalendarEventRequest {
  title: string
  quickDescription: string
  fullDescription?: string
  startDateTime: string
  endDateTime: string
  timezone?: string
  isAllDay?: boolean
  locationName?: string
  latitude?: number
  longitude?: number
  isPublic: boolean
  maxAttendees?: number
  tags?: string[]
  linkedAdventureId?: string
  // Owner (for group/business events)
  ownerEntityId?: string
  ownerEntityType?: EventOwnerEntityType
}

/**
 * Request to update an event
 */
export interface UpdateCalendarEventRequest {
  title?: string
  quickDescription?: string
  fullDescription?: string
  startDateTime?: string
  endDateTime?: string
  timezone?: string
  isAllDay?: boolean
  locationName?: string
  latitude?: number | null
  longitude?: number | null
  isPublic?: boolean
  maxAttendees?: number | null
  coverImageId?: string | null
  tags?: string[]
  linkedAdventureId?: string | null
}

/**
 * Request to set RSVP
 */
export interface SetRsvpRequest {
  status: RsvpStatus
  note?: string
}

/**
 * Request to add/remove tags
 */
export interface EventTagsRequest {
  tagIds: string[]
}

// ====================
// RESPONSE TYPES
// ====================

/**
 * Paginated response for event listing
 */
export type CalendarEventListResponse = PaginatedResponse<CalendarEventDto>

/**
 * Attendees list response
 */
export interface EventAttendeesResponse {
  eventId: string
  attendees: EventAttendeeDto[]
  attendingCount: number
  tentativeCount: number
  notAttendingCount: number
}

// ====================
// CONSTANTS
// ====================

/**
 * Max character limits
 */
export const EVENT_LIMITS = {
  TITLE_MAX_LENGTH: 100,
  QUICK_DESCRIPTION_MAX_LENGTH: 150,
  FULL_DESCRIPTION_MAX_LENGTH: 10000,
  LOCATION_NAME_MAX_LENGTH: 200,
  RSVP_NOTE_MAX_LENGTH: 500,
} as const

/**
 * Default page size for event listings
 */
export const DEFAULT_EVENT_PAGE_SIZE = 20
